/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from "react";
import { useAppTranslation } from "@/hooks/use-app-translation";
import type { LayoutInfo } from "@/lib/layoutRenderer/schemas/layout.schema";
import type { FieldDefinition } from "@/services/form-definition/form-definition-core.types";
import { LISTENER_ACTION_TYPES } from "../constants/dynamicForm.constant";
import { useDynamicFormContext } from "../contexts/DynamicFormContext";
import type {
	FormField,
	PersistedDynamicFieldDefinition,
} from "../interfaces/dynamicForm.interface";
import type { FieldRendererProps } from "../interfaces/renderer.interface";
import { useRendererContext } from "../renderers/cleanRendererSystem";
import { createFieldValidator } from "../utils/fieldValidators.util";
import { persistedDefinitionToFormField } from "../utils/persisted-field-definition.util";
import {
	enforcesRequiredFields,
	showsRequiredIndicator,
} from "../utils/requirement-mode.util";

/** Key for UI-only field props on layout `Field` nodes (see form editor `mergeFieldUiIntoLayout`). */
const LAYOUT_FORM_EDITOR_FIELD_METADATA_KEY = "formEditorField";

function readFormEditorFieldMetadata(
	metadata: LayoutInfo["metadata"],
): Record<string, unknown> | undefined {
	if (!metadata) return undefined;
	const layoutUi = metadata[LAYOUT_FORM_EDITOR_FIELD_METADATA_KEY];
	if (
		layoutUi == null ||
		typeof layoutUi !== "object" ||
		Array.isArray(layoutUi)
	) {
		return undefined;
	}
	return layoutUi as Record<string, unknown>;
}

export function FieldRenderer({
	fieldDefinition,
	layoutInfo,
}: {
	fieldDefinition: FieldDefinition;
	layoutInfo?: LayoutInfo;
}) {
	const { t } = useAppTranslation();
	const dynamicFormContext = useDynamicFormContext?.();
	const hasDynamicFormContext = !!dynamicFormContext;
	const {
		form,
		fieldStates,
		handleFieldChange,
		handleFieldBlur,
		editorMode,
		requirementMode = "require",
	} = dynamicFormContext || {
		form: null,
		fieldStates: {},
		handleFieldChange: () => {},
		handleFieldBlur: () => {},
		editorMode: false,
		requirementMode: "require" as const,
	};

	// Get renderer context
	const { fieldRenderers } = useRendererContext();

	// Get field state from context
	const fieldState = fieldStates[
		fieldDefinition.id as keyof typeof fieldStates
	] as any;

	// Apply rule-based modifications to field (fall back to field defaults if undefined)
	const modifiedField = useMemo(() => {
		const layoutUiRecord = readFormEditorFieldMetadata(layoutInfo?.metadata);

		const modified = {
			...fieldDefinition,
			...(layoutUiRecord ?? {}),
			label: fieldDefinition.label ?? fieldDefinition.name,
			required: fieldState?.isRequired ?? fieldDefinition.required,
			disabled:
				fieldState?.shouldEnable !== undefined
					? !fieldState.shouldEnable
					: layoutInfo?.disabled,
		};

		// Add validation if it exists in field state
		if (fieldState?.validation) {
			(modified as any).validation = fieldState.validation;
		}

		return modified;
	}, [fieldDefinition, fieldState, layoutInfo?.disabled, layoutInfo?.metadata]);

	const formFieldForValidator = useMemo(() => {
		const fromPersisted = persistedDefinitionToFormField(
			fieldDefinition as PersistedDynamicFieldDefinition,
			{ layoutFieldType: layoutInfo?.fieldType },
		);
		const layoutUiRecord = readFormEditorFieldMetadata(layoutInfo?.metadata);

		if (fromPersisted) {
			if (layoutUiRecord) {
				return { ...fromPersisted, ...layoutUiRecord } as FormField;
			}
			return fromPersisted;
		}
		const meta = layoutInfo?.metadata?.field;
		if (meta && typeof meta === "object") return meta as FormField;
		return {
			id: fieldDefinition.id,
			name: fieldDefinition.name,
			label: fieldDefinition.label ?? fieldDefinition.name,
			type: "text",
			required: fieldDefinition.required ?? false,
		} as FormField;
	}, [fieldDefinition, layoutInfo?.fieldType, layoutInfo?.metadata]);

	const fieldForRenderer = useMemo((): FormField => {
		const base = formFieldForValidator;
		const m = modifiedField as Record<string, unknown>;
		return {
			...base,
			label:
				typeof m.label === "string" && m.label.length > 0
					? m.label
					: base.label,
			required: showsRequiredIndicator(
				requirementMode,
				typeof m.required === "boolean" ? m.required : Boolean(base.required),
			),
			disabled: typeof m.disabled === "boolean" ? m.disabled : base.disabled,
			...(m.validation !== undefined ? { validation: m.validation } : {}),
		} as FormField;
	}, [formFieldForValidator, modifiedField, requirementMode]);

	const rendererFieldType =
		layoutInfo?.fieldType ?? fieldForRenderer.type ?? modifiedField.dataType;

	// Determine if field should be shown (fall back to field.hidden if undefined)
	const shouldShow = fieldState?.shouldShow ?? !layoutInfo?.hidden;
	if (!shouldShow) {
		return null;
	}

	const createListeners = () => {
		const listeners: Record<string, (value: unknown) => void> = {};

		const schemaListeners = layoutInfo?.metadata?.listeners as
			| any[]
			| undefined;
		if (schemaListeners && schemaListeners.length > 0) {
			schemaListeners.forEach((listener: any) => {
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const handler = (_value: unknown) => {
					listener.actions.forEach((action: any) => {
						switch (action.type) {
							case LISTENER_ACTION_TYPES.CLEAR_FIELD:
								form.setFieldValue(action.targetFieldId, "");
								break;
							case LISTENER_ACTION_TYPES.SET_FIELD_VALUE:
								if (action.value !== undefined) {
									form.setFieldValue(action.targetFieldId, action.value);
								}
								break;
							case LISTENER_ACTION_TYPES.TOGGLE_CHECKBOX: {
								const currentValue = form.getFieldValue(action.targetFieldId);
								form.setFieldValue(action.targetFieldId, !currentValue);
								break;
							}
						}
					});
				};

				if (listener.debounceMs) {
					let timeoutId: ReturnType<typeof setTimeout>;
					listeners[listener.event] = (value: unknown) => {
						clearTimeout(timeoutId);
						timeoutId = setTimeout(() => handler(value), listener.debounceMs);
					};
				} else {
					listeners[listener.event] = handler;
				}
			});
		}

		const existingOnBlur = listeners.onBlur;
		listeners.onBlur = (value: unknown) => {
			existingOnBlur?.(value);
			handleFieldBlur(fieldForRenderer.name);
		};

		return listeners;
	};

	const listeners = createListeners();

	if (!hasDynamicFormContext) {
		return <div>No dynamic form context</div>;
	}

	// Get the appropriate renderer for this field type
	const FieldRendererComponent = fieldRenderers[rendererFieldType];

	if (!FieldRendererComponent) {
		return (
			<div>No renderer found for field type: {String(rendererFieldType)}</div>
		);
	}

	// Create renderer props
	const rendererProps: FieldRendererProps = {
		field: fieldForRenderer,
		fieldApi: null, // This will be set by the form.Field component
		form,
		fieldStates,
		handleFieldChange,
		editorMode: editorMode || false,
		listeners,
		layoutInfo,
	};

	const fieldValidator = createFieldValidator(formFieldForValidator, t, {
		enforceRequired: enforcesRequiredFields(requirementMode),
	});

	// Render the field using the appropriate renderer
	return (
		<form.Field
			name={fieldForRenderer.name}
			validators={{
				onSubmit: fieldValidator,
				onChange: ({ value }: { value: unknown }) => {
					const error = fieldValidator({ value });
					handleFieldChange(fieldForRenderer.name, value, {
						isValid: error == null,
					});
					return error;
				},
			}}
			listeners={listeners}
			children={(fieldApi: any) => {
				// Update renderer props with fieldApi
				const updatedRendererProps: FieldRendererProps = {
					...rendererProps,
					fieldApi,
				};

				return <FieldRendererComponent {...updatedRendererProps} />;
			}}
		/>
	);
}
